import { useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Upload, Mic, Play, Trash2, X, Sparkles } from 'lucide-react';
import { getToken } from '../lib/api';
import VoiceGreetingPanel from './VoiceGreetingPanel';

const tok = () => ({ Authorization: `Bearer ${getToken()}` });

// Shared audio chooser used by every prompt/greeting/MoH slot in the app.
// Backed by /api/custom-prompts (list/upload/delete) + /:id/stream (play) +
// /record (record from a desk phone). `value` is a prompt_uuid.
type GreetingContext = { name?: string; extension?: string; extension_uuid?: string; state_label?: string };

export default function AudioPicker({ value, onChange, placeholder, disabled, context }:
  { value: string; onChange: (v: string) => void; placeholder?: string; disabled?: boolean; context?: GreetingContext }) {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [busy, setBusy] = useState(false);
  const [recOpen, setRecOpen] = useState(false);
  const [genOpen, setGenOpen] = useState(false);

  const prompts = useQuery<any[]>({
    queryKey: ['custom-prompts'],
    queryFn: async () => { const r = await fetch('/api/custom-prompts', { headers: tok() }); return r.ok ? r.json() : []; },
  });

  // Shared with VoiceGreetingPanel (same query key) — the ✨ Generate button only
  // appears when Orpheus TTS is configured on this server (enabled === true).
  const tts = useQuery<any>({
    queryKey: ['tts-voices'],
    queryFn: async () => { const r = await fetch('/api/tts/voices', { headers: tok() }); return r.ok ? r.json() : { enabled: false }; },
  });

  async function doUpload(file: File) {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('audio', file);
      fd.append('name', file.name.replace(/\.wav$/i, ''));
      const r = await fetch('/api/custom-prompts', { method: 'POST', headers: tok(), body: fd });
      if (r.ok) { const row = await r.json(); await qc.invalidateQueries({ queryKey: ['custom-prompts'] }); onChange(row.prompt_uuid); }
    } finally { setBusy(false); }
  }

  function play() {
    if (!value || !audioRef.current) return;
    audioRef.current.src = `/api/custom-prompts/${value}/stream?token=${encodeURIComponent(getToken() || '')}`;
    audioRef.current.play().catch(() => {});
  }

  return (
    <div className="flex items-center gap-1 mt-2">
      <select className={`input flex-1 max-w-md ${disabled ? 'opacity-60' : ''}`} value={value || ''}
              onChange={e => onChange(e.target.value)} disabled={disabled}>
        <option value="">{placeholder || '— None —'}</option>
        {(prompts.data || []).map((p: any) => <option key={p.prompt_uuid} value={p.prompt_uuid}>{p.name}</option>)}
      </select>
      <input ref={fileRef} type="file" accept=".wav,audio/wav,audio/x-wav" className="hidden"
             onChange={e => { const f = e.target.files?.[0]; if (f) doUpload(f); if (e.currentTarget) e.currentTarget.value = ''; }} />
      <button type="button" title="Play" className="btn-ghost p-2" disabled={disabled || !value} onClick={play}><Play size={14} /></button>
      <button type="button" title="Upload .wav" className="btn-ghost p-2" disabled={!!disabled || busy} onClick={() => fileRef.current?.click()}><Upload size={14} /></button>
      <button type="button" title="Record from a desk phone" className="btn-ghost p-2" disabled={!!disabled || busy} onClick={() => setRecOpen(true)}><Mic size={14} /></button>
      {tts.data?.enabled && (
        <button type="button" title="Generate with Navi" className="btn-ghost p-2" disabled={!!disabled || busy} onClick={() => setGenOpen(true)}><Sparkles size={14} /></button>
      )}
      <button type="button" title="Clear" className="btn-ghost p-2 text-danger" disabled={disabled || !value} onClick={() => onChange('')}><Trash2 size={14} /></button>
      <audio ref={audioRef} className="hidden" />
      {recOpen && <RecordModal onClose={() => setRecOpen(false)}
        onDone={(uuid) => { qc.invalidateQueries({ queryKey: ['custom-prompts'] }); onChange(uuid); setRecOpen(false); }} />}
      {genOpen && <GenerateModal onClose={() => setGenOpen(false)} context={context}
        onSaved={(uuid) => { qc.invalidateQueries({ queryKey: ['custom-prompts'] }); if (uuid) onChange(uuid); setGenOpen(false); }} />}
    </div>
  );
}

// Wraps the Digital-Receptionist voice generator in a modal so any AudioPicker slot
// can generate speech (Orpheus TTS, Navi/tara voice), preview it, save it to the prompt
// library, and have it auto-selected in this picker — without leaving the page.
function GenerateModal({ onClose, onSaved, context }: { onClose: () => void; onSaved: (uuid?: string) => void; context?: GreetingContext }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="card max-w-2xl w-full max-h-[85vh] overflow-y-auto p-5 space-y-3" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-semibold flex items-center gap-2"><Sparkles size={16} /> Generate with Navi</h3>
          <button type="button" className="btn-ghost p-1" onClick={onClose}><X size={16} /></button>
        </div>
        <VoiceGreetingPanel onSaved={onSaved} context={context} />
      </div>
    </div>
  );
}

function RecordModal({ onClose, onDone }: { onClose: () => void; onDone: (uuid: string) => void }) {
  const exts = useQuery<any[]>({ queryKey: ['users'], queryFn: async () => { const r = await fetch('/api/users', { headers: tok() }); return r.ok ? r.json() : []; } });
  const [ext, setExt] = useState('');
  const [name, setName] = useState('');
  const [phase, setPhase] = useState<'idle' | 'ringing' | 'recording'>('idle');
  const [err, setErr] = useState('');
  const cancelled = useRef(false);

  async function go() {
    if (!ext || !name) { setErr('Pick an extension and a name'); return; }
    setPhase('ringing'); setErr('');
    try {
      // Blocks while the extension rings; answers -> beep + recording started.
      const r = await fetch('/api/custom-prompts/record', { method: 'POST', headers: { ...tok(), 'Content-Type': 'application/json' }, body: JSON.stringify({ extension_uuid: ext, name }) });
      if (!r.ok) {
        let msg = await r.text();
        try { msg = JSON.parse(msg).error || msg; } catch {}
        setErr(msg); setPhase('idle'); return;
      }
      const row = await r.json();
      setPhase('recording');
      // Poll until the user hangs up (that finalizes the .wav server-side).
      for (let i = 0; i < 200 && !cancelled.current; i++) {
        await new Promise(ok => setTimeout(ok, 2000));
        const s = await fetch(`/api/custom-prompts/record/status?prompt_uuid=${encodeURIComponent(row.prompt_uuid)}&call_uuid=${encodeURIComponent(row.call_uuid)}`, { headers: tok() });
        if (!s.ok) continue;
        const st = await s.json();
        if (st.done) { onDone(row.prompt_uuid); return; }
        if (!st.recording && !st.done) { setErr('Call ended but no audio was recorded — try again.'); setPhase('idle'); return; }
      }
      if (!cancelled.current) { setErr('Timed out waiting for the recording to finish.'); setPhase('idle'); }
    } catch (e: any) { setErr(e.message); setPhase('idle'); }
  }
  const busy = phase !== 'idle';
  function close() { cancelled.current = true; onClose(); }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={close}>
      <div className="card max-w-md w-full p-5 space-y-3" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-semibold">Record from a desk phone</h3>
          <button type="button" className="btn-ghost p-1" onClick={close}><X size={16} /></button>
        </div>
        <p className="text-sm text-textSecondary">We'll ring the chosen extension. Answer, record after the tone, then hang up — it's saved as a prompt.</p>
        <label className="block text-sm">Extension
          <select className="input mt-1" value={ext} onChange={e => setExt(e.target.value)} disabled={busy}>
            <option value="">— Choose —</option>
            {(exts.data || []).map((u: any) => <option key={u.extension_uuid} value={u.extension_uuid}>{u.extension} — {u.name || ''}</option>)}
          </select>
        </label>
        <label className="block text-sm">Prompt name
          <input className="input mt-1" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. main-greeting" disabled={busy} />
        </label>
        {phase === 'recording' && <div className="text-sm text-textSecondary">Recording after the tone — hang up when you're done.</div>}
        {err && <div className="text-danger text-xs break-all">{err}</div>}
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={close}>Cancel</button>
          <button type="button" className="btn-primary" disabled={busy} onClick={go}>
            {phase === 'ringing' ? 'Ringing…' : phase === 'recording' ? 'Recording…' : 'Call & Record'}
          </button>
        </div>
      </div>
    </div>
  );
}
